import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { faClockRotateLeft } from "@fortawesome/pro-regular-svg-icons";

import { IMPORTED_CONVERSATION_HISTORY_LIMIT } from "@signalist/shared-types";

import { Icon } from "@/components";
import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { classNames } from "@/utils/classNames";

import { EarlierConversationResultLine } from "../utils/earlierConversationResult.util";

interface EarlierConversationBarProps {
	isLoading: boolean;
	isBusy: boolean;
	requestFailed: boolean;
	/** Only the newest imported messages are shown; the server left older ones out. */
	truncated?: boolean;
	/** Whether what is loaded here is shared with the team (true) or only the viewer's (false). */
	shared?: boolean | null;
	resultLines: EarlierConversationResultLine[];
	onLoad: () => void;
}

/**
 * "Load earlier conversation" at the top of a lead's chat (PREC-4305): pulls the viewer's own
 * LinkedIn and mailbox history with the lead into the thread, on request only. Below the button,
 * one line per source says what happened; a lookup that failed reads as an error, never as
 * "nothing found".
 */
export const EarlierConversationBar: FunctionComponent<EarlierConversationBarProps> = ({
	isLoading,
	isBusy,
	requestFailed,
	truncated,
	shared,
	resultLines,
	onLoad,
}) => {
	const { t } = useTranslation();

	return (
		<div
			className="w-full flex flex-col items-center gap-1 py-1"
			data-testid="earlier-conversation"
		>
			<Button
				color={ButtonColor.ACTION_SECONDARY}
				disabled={isBusy}
				image={<Icon className="mr-2" icon={faClockRotateLeft} />}
				isLoading={isLoading}
				size={ButtonSize.XS}
				testId="earlier-conversation-load"
				// Never "submit": the chat can sit inside a form (the composer, the extension panel).
				type="button"
				title={
					isLoading
						? t("inbox.thread.earlierConversation.loading")
						: t("inbox.thread.earlierConversation.load")
				}
				onClick={onLoad}
			/>
			{typeof shared === "boolean" && (
				<span className="text-xs text-text-meta" data-testid="earlier-conversation-visibility">
					{t(
						shared
							? "inbox.thread.earlierConversation.sharedNote"
							: "inbox.thread.earlierConversation.privateNote"
					)}
				</span>
			)}
			{truncated && (
				<span className="text-xs text-text-meta" data-testid="earlier-conversation-truncated">
					{t("inbox.thread.earlierConversation.truncated", {
						count: IMPORTED_CONVERSATION_HISTORY_LIMIT,
					})}
				</span>
			)}
			{requestFailed && (
				<span className="text-xs text-status-error" role="alert">
					{t("inbox.thread.earlierConversation.requestFailed")}
				</span>
			)}
			{!requestFailed &&
				resultLines.map((line) => (
					<span
						key={line.source}
						className={classNames("text-xs", line.isError ? "text-status-error" : "text-text-meta")}
						data-testid={`earlier-conversation-${line.source}`}
						role={line.isError ? "alert" : undefined}
					>
						{t(line.key, { count: line.count })}
					</span>
				))}
		</div>
	);
};
